import { Project } from "@/types";
import {
  CalendarStar,
  MapPin,
  CurrencyCircleDollar,
} from "@phosphor-icons/react/dist/ssr";
import Link from "next/link";
import { roundToNearest } from "@/app/utils/format-number";
import { sqmToSqft } from "@/app/utils/area";
import { convertDateStringToMonthYear } from "@/app/utils/date";
import Image from "next/legacy/image";
import PricingExperiment from "./ProjectDetails/PricingExperiment";
import { convertToLacCrore } from "@/app/utils/currency";

export default function ProjectCard({ project }: { project: Project }) {
  const { fileDetails, floorplanConfigurations = [], priceSummary, reviewPreview } = project;
  const { projectAddress } = fileDetails;
  const area = projectAddress.village || projectAddress.locality;
  const bedrooms = new Set(
    floorplanConfigurations
      .map((config) => config.rooms?.["bedrooms"])
      .filter((x) => !!x)
  );
  const areas = floorplanConfigurations.map((config) => config.area);
  const maxArea = Math.max(...areas);
  const minArea = Math.min(...areas);

  return (
    <div className="bg-white rounded-xl overflow-hidden w-full max-w-sm shadow-md relative">
      {/* Ribbon for reviewPreview */}
      {project.reviewPreview && (
        <div className="absolute left-0 top-0 z-20">
          <div className="bg-gradient-to-r from-pink-500 to-yellow-400 text-white font-bold px-4 py-1 rounded-br-xl shadow-lg text-xs tracking-wide">
            ⭐ Review Available
          </div>
        </div>
      )}
      <div className="relative w-full h-72">
        <Image
          src={project.heroImageUrl || "/images/placeholder.svg"}
          alt={project.projectName}
          layout="fill"
          className="object-cover"
          priority
        />
        <div className="absolute inset-0 bg-linear-to-t from-black/70 to-transparent" />
        <div className="absolute bottom-4 left-4 right-4">
          {bedrooms.size > 0 && (
            <span className="mb-2 inline-flex rounded-full bg-white/90 px-2.5 py-0.5 text-xs font-medium text-gray-900">
              {Array.from(bedrooms)[0]}BHK
            </span>
          )}
          {!floorplanConfigurations && (
            <span className="badge badge-accent badge-sm">Coming Soon</span>
          )}
          <h2 className="flex items-center gap-2 text-2xl font-semibold text-white">
            {project.alias || project.projectName}
          </h2>
          <p className="text-white text-xs capitalize">
            {project.developerName?.toLowerCase()}
          </p>
        </div>
      </div>

      <div className="p-4">
        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between text-gray-500 text-sm">
            <div className="flex items-center gap-2">
              <MapPin weight="fill" className="w-5 h-5 text-gray-600" />
              <span className="text-sm capitalize">{area?.toLowerCase()}</span>
            </div>

            <PricingExperiment>
              {priceSummary && priceSummary.minPrice && (
                <>
                  <div className="divider m-0 h-2" />
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-blue-600">
                      <span className="text-blue-600 font-bold">₹</span>
                      <span className="font-semibold">
                        From {convertToLacCrore(priceSummary.minPrice)}
                      </span>
                    </div>
                  </div>
                </>
              )}
            </PricingExperiment>
          </div>
          <div className="divider m-0 h-2" />
          <div className="flex items-center justify-between text-gray-500 text-sm">
            <div className="flex items-center gap-2">
              <CalendarStar weight="fill" className="w-5 h-5 " />
              <span className="">
                {convertDateStringToMonthYear(
                  project.fileDetails?.projectDetails?.proposedDateOfCompletion
                )}
              </span>
            </div>
            <div className="">
              {areas.length > 0 && (
                <span>
                  {roundToNearest(sqmToSqft(minArea))} sft -{" "}
                  {roundToNearest(sqmToSqft(maxArea))} sft
                </span>
              )}
            </div>
          </div>

          <Link href={`/project/${project.uuid}`} className="w-full mt-2">
            <button className="btn btn-primary w-full normal-case">
              {reviewPreview ? "See Review" : "View Details"}
            </button>
          </Link>
        </div>
      </div>
    </div>
  );
}
